import React from "react";
import { useLang } from "@/i18n/LanguageProvider";
import { formatIDR } from "@/i18n/FxProvider";
import { SectionTitle, Reveal } from "@/components/Reveal";
import { waUrl, LAUNDRY_MESSAGE } from "@/lib/contact";
import { Truck, Sparkles, Shirt, Footprints, Flame, Leaf, Smile, Sun } from "lucide-react";
import { LAUNDRY_KILO, LAUNDRY_SHOES } from "@/lib/menu";
import { BRAND_LOGOS } from "@/lib/images";

function PriceRow({ icon: Icon, name, price, unit, note }) {
  return (
    <div className="flex items-start gap-4 py-5 border-b border-sand last:border-b-0" data-testid={`laundry-row-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-11 h-11 rounded-full bg-moss/10 grid place-items-center text-moss shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg text-ink">{name}</div>
        {note && <div className="text-xs text-ink/55 mt-0.5">{note}</div>}
      </div>
      <div className="text-right">
        <div className="font-display text-lg text-ink whitespace-nowrap">{formatIDR(price)}</div>
        <div className="text-[11px] text-ink/50 uppercase tracking-widest">{unit}</div>
      </div>
    </div>
  );
}

const KG_ICONS = [Sparkles, Shirt, Flame, Flame];
const SHOE_ICONS = [Footprints, Footprints];

export default function Laundry() {
  const { t } = useLang();

  return (
    <div className="pb-24" data-testid="laundry-page">
      {/* Hero with logo */}
      <section className="container-px mx-auto pt-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-[#F2F6FD] via-cream to-sand/40 border border-sand p-8 md:p-14">
            <div className="absolute -right-16 -top-16 w-96 h-96 rounded-full bg-[#1E3A8A]/5 blur-3xl" />
            <div className="relative grid md:grid-cols-5 gap-10 items-center">
              <div className="md:col-span-3">
                <div className="eyebrow mb-3">{t.laundry.eyebrow}</div>
                <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">{t.laundry.title}</h1>
                <p className="mt-3 italic text-moss text-base md:text-lg">{t.laundry.slogan}</p>
                <p className="text-ink/70 mt-5 text-[15px] leading-relaxed max-w-xl">{t.laundry.subtitle}</p>

                <div className="flex flex-wrap gap-2 mt-6">
                  {t.laundry.services.map((s) => (
                    <span key={s} className="px-3 py-1.5 bg-white rounded-full text-[11px] uppercase tracking-widest text-ink/70 border border-sand">
                      {s}
                    </span>
                  ))}
                </div>

                <a
                  data-testid="laundry-wa-btn"
                  href={waUrl(LAUNDRY_MESSAGE("Standard"))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-moss text-cream text-sm hover:bg-ink transition-colors"
                >
                  <Truck className="w-4 h-4" /> {t.laundry.pickupTitle}
                </a>
              </div>

              <div className="md:col-span-2 grid place-items-center">
                <div className="aspect-square w-full max-w-xs rounded-2xl bg-white border border-sand grid place-items-center p-8 shadow-sm">
                  <img
                    src={BRAND_LOGOS.laundryLogo}
                    alt="Agung Wiwin Laundry"
                    className="w-full h-full object-contain"
                    data-testid="laundry-logo"
                  />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Pricing grid */}
      <section className="container-px mx-auto mt-16 grid lg:grid-cols-2 gap-10">
        {/* Wash per kg */}
        <Reveal>
          <div className="bg-white rounded-3xl border border-sand p-7 md:p-9">
            <div className="eyebrow mb-2">Price list</div>
            <h2 className="font-display text-2xl md:text-3xl text-ink mb-6">{t.laundry.kiloTitle}</h2>
            <div>
              {LAUNDRY_KILO.map((item, i) => (
                <PriceRow
                  key={item.name}
                  icon={KG_ICONS[i] || Sparkles}
                  name={item.name}
                  price={item.price}
                  unit={item.unit}
                  note={item.eta}
                />
              ))}
            </div>
          </div>
        </Reveal>

        {/* Shoe cleaning */}
        <Reveal delay={0.1}>
          <div className="bg-white rounded-3xl border border-sand p-7 md:p-9">
            <div className="eyebrow mb-2">Shoe cleaning</div>
            <h2 className="font-display text-2xl md:text-3xl text-ink mb-2">{t.laundry.shoesTitle}</h2>
            <p className="text-ink/60 text-sm mb-6">{t.laundry.shoesSubtitle}</p>
            <div>
              {LAUNDRY_SHOES.map((item, i) => (
                <PriceRow
                  key={item.name}
                  icon={SHOE_ICONS[i] || Footprints}
                  name={item.name}
                  price={item.price}
                  unit={item.unit}
                  note={item.eta}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Benefits trio */}
      <section className="container-px mx-auto mt-16">
        <Reveal>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Leaf, t: "Fresh and clean" },
              { icon: Smile, t: "More confidence" },
              { icon: Sun, t: "Brighter days" },
            ].map((b) => (
              <div key={b.t} className="bg-cream rounded-2xl p-6 border border-sand text-center">
                <div className="w-10 h-10 rounded-full bg-moss/10 grid place-items-center text-moss mx-auto mb-3">
                  <b.icon className="w-5 h-5" />
                </div>
                <div className="font-display text-ink">{b.t}</div>
              </div>
            ))}
          </div>
          <p className="text-center italic text-moss mt-6 font-display text-lg">{t.laundry.tagline}</p>
        </Reveal>
      </section>

      {/* Pickup + Finishing + Caveat */}
      <section className="container-px mx-auto mt-16 grid md:grid-cols-3 gap-5">
        <Reveal>
          <div className="bg-white rounded-2xl border border-sand p-6 h-full">
            <Truck className="w-6 h-6 text-moss mb-3" />
            <div className="font-display text-xl text-ink mb-1.5">{t.laundry.pickupTitle}</div>
            <p className="text-ink/70 text-sm leading-relaxed">{t.laundry.pickupNote}</p>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="bg-white rounded-2xl border border-sand p-6 h-full">
            <Sparkles className="w-6 h-6 text-moss mb-3" />
            <div className="font-display text-xl text-ink mb-1.5">{t.laundry.finishingTitle}</div>
            <p className="text-ink/70 text-sm leading-relaxed">{t.laundry.finishingNote}</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-sand/60 rounded-2xl border border-sand p-6 h-full">
            <div className="eyebrow mb-2">{t.laundry.caveatTitle}</div>
            <p className="text-ink/70 text-sm leading-relaxed">{t.laundry.caveatNote}</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
